import { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import MemberDetailRow from "../common/MemberDetailRow";

function formatCharges(charges) {
    if (!charges) return 0;
    if (typeof charges === "number") return charges;
    if (typeof charges === "string") {
        const num = parseFloat(charges);
        return isNaN(num) ? charges : num;
    }
    if (Array.isArray(charges)) {
        return charges.reduce((acc, item) => acc + Number(item.amt || 0), 0);
    }
    if (typeof charges === "object") {
        return charges.amt || 0;
    }
    return 0;
}

export default function MemberInfo({ member }) {
    const [showModal, setShowModal] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (showModal) {
            const timer = setTimeout(() => setIsOpen(true), 10);
            return () => clearTimeout(timer);
        } else {
            setIsOpen(false);
        }
    }, [showModal]);

    if (!member) return null;

    const damageTotal = formatCharges(member.damageCharges);
    const overdue_charges = formatCharges(member.overdue_charges);

    const hasOverdueDetails = Array.isArray(member.overdue_charges_details) && member.overdue_charges_details.length > 0;

    const openDetails = () => {
        if (hasOverdueDetails) {
            setShowModal(true);
        }
    };

    const closeDetails = () => {
        setIsOpen(false);
        setTimeout(() => setShowModal(false), 300);
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">
                Member Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <MemberDetailRow label="Member Name" value={member.name} />
                <MemberDetailRow label="Phone Number" value={member.phone} />

                <MemberDetailRow label="Email ID" value={member.email} />
                <MemberDetailRow label="Date of Birth" value={member.dob || "-"} />

                <MemberDetailRow label="Subscription Plan" value={member.subscription || "N/A"} />
                <MemberDetailRow label="Deposit" value={member.deposit} />

                <MemberDetailRow label="Damage Charges" value={<span className="text-blue-600 underline cursor-pointer"> ₹{damageTotal} </span>} />
                <MemberDetailRow label="Credits" value={<span className="font-medium text-green-600"> {member.credits} </span>} />

                <MemberDetailRow
                    label="Overdue Charges"
                    value={
                        hasOverdueDetails ? (
                            <button
                                onClick={openDetails}
                                className="text-blue-600 underline cursor-pointer hover:text-blue-800 transition-colors focus:outline-none text-left font-medium"
                            >
                                ₹{overdue_charges}
                            </button>
                        ) : (
                            <span className="text-gray-800">
                                ₹{overdue_charges}
                            </span>
                        )
                    }
                />
                <MemberDetailRow label="Billing Address" value={member.billing_address} />
            </div>

            {/* Overdue Details Modal */}
            {showModal && (
                <div
                    className={`fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"
                        }`}
                    onClick={closeDetails}
                >
                    <div
                        className={`w-full max-w-4xl rounded-xl bg-white shadow-xl overflow-hidden transition-all duration-300 transform ${isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95"
                            }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Overdue Details</h3>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Detailed overdue records for {member.name}
                                </p>
                            </div>
                            <button
                                onClick={closeDetails}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-6 overflow-y-auto max-h-[60vh]">
                            <div className="overflow-x-auto border border-gray-100 rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200 text-xs text-left">
                                    <thead className="bg-gray-50 text-gray-700 font-semibold uppercase tracking-wider">
                                        <tr>
                                            <th className="px-4 py-3">Book Info</th>
                                            <th className="px-4 py-3 text-center">Credits</th>
                                            <th className="px-4 py-3 text-center">Issue / Due Date</th>
                                            <th className="px-4 py-3 text-center">Overdue Days</th>
                                            <th className="px-4 py-3 text-right">Charge</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white text-gray-600">
                                        {member.overdue_charges_details.map((item, index) => (
                                            <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <img
                                                            src={item.book_image || "https://via.placeholder.com/48x64"}
                                                            alt={item.book_name}
                                                            className="w-10 h-14 object-cover rounded border border-gray-150 flex-shrink-0"
                                                        />
                                                        <div className="min-w-0">
                                                            <p className="font-semibold text-gray-800 text-sm truncate max-w-[280px]" title={item.book_name}>
                                                                {item.book_name || "—"}
                                                            </p>
                                                            <div className="flex flex-wrap gap-x-2 text-[10px] text-gray-400 mt-0.5">
                                                                <span>Product ID: {item.product_id || "N/A"}</span>
                                                                <span>•</span>
                                                                <span>Copy ID: {item.copy_post_id || "N/A"}</span>
                                                                {item.copy_smart_id && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span>Smart ID: {item.copy_smart_id}</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center text-sm font-medium text-blue-600">
                                                    {item.credits || 0}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <div className="space-y-0.5">
                                                        <span className="block font-medium text-gray-700">Issue: {item.issue_date || "—"}</span>
                                                        <span className="block text-gray-500">Due: {item.due_date || "—"}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700">
                                                        {item.days_overdue || 0} days
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                                                    ₹{item.overdue_charge || 0}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Footer Summary */}
                        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t border-gray-100">
                            <span className="text-sm font-medium text-gray-500">Total Items: {member.overdue_charges_details.length}</span>
                            <div className="flex items-center gap-3">
                                <span className="text-sm font-semibold text-gray-700">Total Overdue Charges:</span>
                                <span className="text-lg font-bold text-red-600">₹{overdue_charges}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
